// react
import { ReactNode, useContext, useMemo, useState } from 'react';

// types
import { ActionButton, Contract, Entity, Frame, Option, Pagination } from '../../../../@types';

// i18next
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// context
import {
	ContextProject,
	ContextUserProjectPermissions,
} from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// cards
import { CardContract } from '../../../cards/CardContract/CardContract';
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';

// ui
import { BtnExport } from '../../../ui/BtnExport/BtnExport';
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';

// icons
import { AssignmentOutlined as IconAssignment } from '@mui/icons-material';
import { DialogEditContract } from '../../../dialogs/DialogEditContract/DialogEditContract';

// props
export interface TableContractsProps {
	contracts: Contract[];
	frame?: Frame;
	hasExport?: boolean;
	isLoading?: boolean;
	pagination: Pagination;
	refetchContracts?: () => void;
}

export const TableContracts = ({
	contracts,
	frame = 'PROJECT',
	hasExport = false,
	isLoading,
	pagination,
	refetchContracts,
}: TableContractsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramEntities = searchParams.get('entities');
	const paramQ = searchParams.get('q');
	const paramStatus = searchParams.get('status');

	// defaults
	const defaultFiltersValues = {
		entities: [],
		status: [],
	};

	// state
	const [filtersValues, setFiltersValues] = useState({
		entities: paramEntities ? paramEntities.split(',') : defaultFiltersValues.entities,
		status: paramStatus ? paramStatus.split(',') : defaultFiltersValues.status,
	});
	const [suppliers, setSuppliers] = useState<Entity[]>([]);

	// fetch suppliers
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&type=SUPPLIER&fields=id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setSuppliers(res.data);
			}
		},
	});

	// options
	const optionsSuppliers: Option[] = suppliers.map((supplier) => {
		return {
			label: supplier.name,
			value: String(supplier.id),
		};
	});

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});

		// Update URL search params
		if (Array.isArray(value) && value.length) {
			searchParams.set(name, value.join(','));
		} else {
			searchParams.delete(name);
		}
		setSearchParams(searchParams);
	};

	const idFormFilters = 'form-contracts-filters';

	// filters
	const filters: FilterProps[] = [];

	if (frame === 'SUPPLIERS') {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-entities`,
			label: t('suppliers'),
			name: 'entities',
			multiple: true,
			onChange: handleChangeFilterValue,
			options: optionsSuppliers,
			type: 'SELECT',
			value: filtersValues.entities,
		});
	}

	filters.push({
		hasAllOption: true,
		hasNoneOption: false,
		hasSearchParam: true,
		id: `${idFormFilters}-status`,
		label: t('status'),
		name: 'status',
		multiple: true,
		onChange: handleChangeFilterValue,
		options: [
			{
				label: t('draft'),
				value: 'DRAFT',
			},
			{
				label: t('submitted'),
				value: 'SUBMITTED',
			},
			{
				label: t('active'),
				value: 'ACTIVE',
			},
		],
		type: 'SELECT',
		value: filtersValues.status,
	});

	const hasFilters = filters.length > 0;

	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: frame === 'SUPPLIERS' ? 2 : 3,
			},
			isSortable: true,
			label: t('contract'),
			value: 'num',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('status'),
			value: 'status',
		},
	];

	if (frame === 'SUPPLIERS') {
		headers.push({
			GridProps: {
				lg: 1.5,
			},
			label: t('suppliers'),
			value: 'entities',
		});
	}

	headers.push(
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('paid'),
			value: 'paid',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('amount'),
			value: 'amount',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('stepId'),
			value: 'idStep',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('signingDate'),
			value: 'dateSigning',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('updated'),
			value: 'modified',
		}
	);

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: { children: t('empty.contracts.content') },
			IconCircleProps: { icon: (<IconAssignment />) as ReactNode },
			title: { children: t('empty.contracts.title') },
		};

		if (userProjectPermissions?.contract?.create && emptyProps.actions) {
			emptyProps.actions.push({
				children: t('addContract'),
				onClick: () => {
					searchParams.set('dialog', 'addContract');
					setSearchParams(searchParams);
				},
				variant: 'contained' as ActionButton['variant'],
			});
		}

		if (paramQ || paramStatus) {
			emptyProps.actions = [];
			emptyProps.content = { children: t('empty.default.content') };
			emptyProps.IconCircleProps = { icon: null };
		}

		return emptyProps;
	}, [paramQ, paramStatus, searchParams, setSearchParams, t, userProjectPermissions]);

	const content = useMemo(() => {
		if (isLoading || !contracts.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{contracts.map((contract, i) => {
					return (
						<Grid item key={`contract-${contract.id}-${i}`} xs={12}>
							<CardContract contract={contract} frame={frame} refetchContracts={refetchContracts} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [contracts, empty, frame, isLoading, refetchContracts]);

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={12} md={hasExport || hasFilters ? true : 12}>
					<FormSearch InputProps={{ placeholder: t('searchById') }} />
				</Grid>
				{hasExport && (
					<Grid item xs={6} md="auto">
						<BtnExport resource="CONTRACT" />
					</Grid>
				)}
				{hasFilters && (
					<Grid item xs={6} md="auto">
						<BtnFilters
							filters={filters}
							onReset={() => {
								setFiltersValues(defaultFiltersValues);
								searchParams.delete('status');
								searchParams.delete('entities');
								setSearchParams(searchParams);
							}}
						/>
					</Grid>
				)}
				<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
					<TableHeaders headers={headers} />
				</Grid>
				<Grid item xs={12}>
					{content}
				</Grid>
				<Grid item xs={12}>
					<TableFooter numShowing={contracts.length} pagination={pagination} />
				</Grid>
			</Grid>
			<DialogEditContract
				isOpen={paramDialog === 'editContract'}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('dialogContract');
					setSearchParams(searchParams);
				}}
				onSuccess={() => {
					if (refetchContracts) {
						refetchContracts();
					}
				}}
			/>
		</>
	);
};
